import Slider from '@react-native-community/slider';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { PrimaryCTA } from '@/components/onboarding/Controls';
import { Eyebrow, H1, Screen } from '@/components/onboarding/Screen';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useOnboarding } from '@/store/onboarding';

export default function SizeScreen() {
  const router = useRouter();
  const setSqft = useOnboarding((s) => s.setSqft);
  const setBedrooms = useOnboarding((s) => s.setBedrooms);
  const setBathrooms = useOnboarding((s) => s.setBathrooms);
  const setStories = useOnboarding((s) => s.setStories);
  const stored = useOnboarding((s) => s.sqft);
  const storedBeds = useOnboarding((s) => s.bedrooms);
  const storedBaths = useOnboarding((s) => s.bathrooms);
  const storedStories = useOnboarding((s) => s.stories);
  const [val, setVal] = useState<number>(stored);
  const [beds, setBeds] = useState<number | null>(storedBeds);
  const [baths, setBaths] = useState<number | null>(storedBaths);
  const [stories, setStoriesLocal] = useState<number | null>(storedStories);

  const display = val >= 6000 ? '6,000+' : val.toLocaleString();

  const onContinue = () => {
    setSqft(val);
    setBedrooms(beds);
    setBathrooms(baths);
    setStories(stories);
    router.push('/(onboarding)/hvac' as never);
  };

  return (
    <Screen
      scroll
      footer={<PrimaryCTA label="Continue" onPress={onContinue} />}>
      <Eyebrow>4 · Your home</Eyebrow>
      <H1>How big is{'\n'}the home?</H1>

      <View style={styles.value}>
        <Text style={styles.big}>{display}</Text>
        <Text style={styles.unit}>SQUARE FEET</Text>
      </View>

      <View style={styles.sliderWrap}>
        <Slider
          minimumValue={500}
          maximumValue={6000}
          step={100}
          value={val}
          onValueChange={(v) => setVal(Math.round(v))}
          minimumTrackTintColor={Palette.forest}
          maximumTrackTintColor={Palette.sandDeep}
          thumbTintColor={Palette.forest}
        />
        <View style={styles.ends}>
          <Text style={styles.endLabel}>500</Text>
          <Text style={styles.endLabel}>6,000+</Text>
        </View>
      </View>

      <Text style={styles.sectionLabel}>BEDROOMS</Text>
      <ChipRow
        options={[1, 2, 3, 4, 5, 6]}
        labelMore="7+"
        value={beds}
        onPick={(n) => setBeds(n)}
      />

      <Text style={styles.sectionLabel}>BATHROOMS</Text>
      <ChipRow
        options={[1, 1.5, 2, 2.5, 3, 3.5]}
        labelMore="4+"
        value={baths}
        onPick={(n) => setBaths(n)}
      />

      {/*
        Stories — number of above-grade levels. Influences roof
        inspection difficulty hints (a 3-story house = ladder hazard)
        and structural-walk task scoping. Most homes are 1 or 2.
      */}
      <Text style={styles.sectionLabel}>STORIES</Text>
      <ChipRow
        options={[1, 2, 3]}
        labelMore="4+"
        value={stories}
        onPick={(n) => setStoriesLocal(n)}
      />

    </Screen>
  );
}

/**
 * Always-one-row chip strip. Old version used flex-wrap which broke
 * bedroom/bathroom counts onto a second row on narrow phones — visually
 * messy and made the screen feel longer than it is. Horizontal scroll
 * keeps them on a single tidy line; tiny chip padding makes 6+1 fit
 * without scrolling on most phones, and scroll kicks in only when it
 * has to.
 */
function ChipRow({
  options,
  labelMore,
  value,
  onPick,
}: {
  options: number[];
  labelMore: string;
  value: number | null;
  onPick: (n: number | null) => void;
}) {
  const max = options[options.length - 1];
  const isMore = value != null && value > max;
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      // flexGrow: 0 keeps the ScrollView from vertically filling its
      // flex parent (which made chips render as tall stretched pills).
      // alignSelf: 'flex-start' shrink-wraps the row instead of letting
      // it span the column width.
      style={styles.chipScroll}
      contentContainerStyle={styles.chips}>
      {options.map((n) => {
        const sel = value === n;
        const label = Number.isInteger(n) ? `${n}` : n.toString();
        return (
          <Pressable
            key={n}
            onPress={() => onPick(sel ? null : n)}
            style={({ pressed }) => [
              styles.chip,
              sel && styles.chipSel,
              pressed && { opacity: 0.85 },
            ]}>
            <Text style={[styles.chipText, sel && styles.chipTextSel]}>{label}</Text>
          </Pressable>
        );
      })}
      <Pressable
        onPress={() => onPick(isMore ? null : max + 1)}
        style={({ pressed }) => [
          styles.chip,
          isMore && styles.chipSel,
          pressed && { opacity: 0.85 },
        ]}>
        <Text style={[styles.chipText, isMore && styles.chipTextSel]}>{labelMore}</Text>
      </Pressable>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  value: {
    alignItems: 'center',
    marginTop: Spacing.six,
  },
  big: {
    fontFamily: Fonts.serifBold,
    fontSize: 54,
    color: Palette.forest,
    letterSpacing: -1,
  },
  unit: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkFaint,
    letterSpacing: 0.5,
    marginTop: 2,
  },
  sliderWrap: {
    marginTop: Spacing.five,
  },
  ends: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  endLabel: {
    fontSize: 12,
    fontFamily: Fonts.sansSemibold,
    color: Palette.inkFaint,
  },
  sectionLabel: {
    marginTop: Spacing.five,
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    letterSpacing: 0.8,
    marginBottom: 10,
  },
  chipScroll: {
    // Critical: without these the ScrollView greedily fills the column
    // height, which stretched each chip into a tall pill (see Wave 8
    // bug-report screenshot).
    flexGrow: 0,
    alignSelf: 'stretch',
  },
  chips: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    // Bleed a bit of right padding so the last chip never sits flush
    // against the screen edge when the row is scrollable.
    paddingRight: 16,
  },
  chip: {
    // Tighter horizontal padding (was 16) so 6 chips + an overflow chip
    // fit in one row on most phones without horizontal scroll kicking in.
    paddingHorizontal: 13,
    paddingVertical: 11,
    borderRadius: Radius.pill,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    minWidth: 48,
    alignItems: 'center',
  },
  chipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  chipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  chipTextSel: {
    color: Palette.cream,
  },
});
